import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  DASHBOARD_APP_CATALOG,
  DASHBOARD_APP_PROFILES,
  DASHBOARD_APP_VERSION,
  getAllDashboardAppIds,
  normalizeDashboardAppIds,
} from "./dashboardApps";
import "./DashboardAppSelectorModal.scss";

const PROFILE_ICON_PATHS = {
  club: (
    <>
      <path d="M8 5h8v3.5a4 4 0 0 1-8 0V5z" />
      <path d="M8 7H5.5a2 2 0 0 0 2 3.5H8" />
      <path d="M16 7h2.5a2 2 0 0 1-2 3.5H16" />
      <path d="M12 12.5V16" />
      <path d="M9 19h6" />
      <path d="M10 16h4v3h-4z" />
    </>
  ),
  bar_restaurant: (
    <>
      <path d="M7 4v7" />
      <path d="M5 4v4a2 2 0 0 0 4 0V4" />
      <path d="M7 11v9" />
      <path d="M15 4v16" />
      <path d="M15 4c2.2 1.4 3 3.1 3 5.5V11h-3" />
    </>
  ),
  event: (
    <>
      <path d="M7 3v4" />
      <path d="M17 3v4" />
      <path d="M5 6h14v14H5z" />
      <path d="M5 10h14" />
      <path d="m9 15 2 2 4-5" />
    </>
  ),
  ticketing: (
    <>
      <path d="M5 7h14v4a2 2 0 0 0 0 4v4H5v-4a2 2 0 0 0 0-4z" />
      <path d="M10 9v6" />
      <path d="M14 9v6" />
    </>
  ),
  advanced: (
    <>
      <path d="M5 7h14" />
      <path d="M5 12h14" />
      <path d="M5 17h14" />
      <path d="M9 5v4" />
      <path d="M15 10v4" />
      <path d="M11 15v4" />
      <path d="M7.5 7h3" />
      <path d="M13.5 12h3" />
      <path d="M9.5 17h3" />
    </>
  ),
};

const ProfileIcon = ({ profileId }) => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    {PROFILE_ICON_PATHS[profileId] || PROFILE_ICON_PATHS.advanced}
  </svg>
);

function DashboardAppSelectorModal({
  isOpen,
  mode = "settings",
  initialSelectedAppIds,
  initialProfile,
  isSaving = false,
  onClose,
  onSave,
}) {
  const { t } = useTranslation("dashboardApps");
  const defaultProfile = initialProfile || DASHBOARD_APP_PROFILES[0].id;
  const defaultSelectedIds = normalizeDashboardAppIds(initialSelectedAppIds);
  const [profile, setProfile] = useState(defaultProfile);
  const [selectedAppIds, setSelectedAppIds] = useState(
    defaultSelectedIds.length > 0 ? defaultSelectedIds : DASHBOARD_APP_PROFILES[0].appIds,
  );

  const selectedSet = useMemo(() => new Set(selectedAppIds), [selectedAppIds]);
  const isOnboarding = mode === "onboarding";

  useEffect(() => {
    if (!isOpen) return;

    const nextSelectedIds = normalizeDashboardAppIds(initialSelectedAppIds);
    setProfile(initialProfile || DASHBOARD_APP_PROFILES[0].id);
    setSelectedAppIds(
      nextSelectedIds.length > 0 ? nextSelectedIds : DASHBOARD_APP_PROFILES[0].appIds,
    );
  }, [initialProfile, initialSelectedAppIds, isOpen]);

  if (!isOpen) return null;

  const applyProfile = (profileItem) => {
    setProfile(profileItem.id);
    setSelectedAppIds(profileItem.appIds);
  };

  const toggleApp = (appId) => {
    setSelectedAppIds((current) => {
      if (current.includes(appId)) {
        return current.filter((id) => id !== appId);
      }
      return [...current, appId];
    });
  };

  const save = (appIds = selectedAppIds) => {
    const normalized = normalizeDashboardAppIds(appIds);
    if (normalized.length === 0 || isSaving) return;
    onSave?.({
      selectedAppIds: normalized,
      profile,
      version: DASHBOARD_APP_VERSION,
      preferencesConfirmedAt: Date.now(),
    });
  };

  return (
    <div className="dashboard-app-selector-overlay" role="dialog" aria-modal="true">
      <div className="dashboard-app-selector-modal">
        <div className="dashboard-app-selector-hero">
          <div>
            <p>{t(isOnboarding ? "onboardingEyebrow" : "settingsEyebrow")}</p>
            <h2>{t(isOnboarding ? "onboardingTitle" : "settingsTitle")}</h2>
            <span>{t(isOnboarding ? "onboardingSubtitle" : "settingsSubtitle")}</span>
          </div>
          {!isOnboarding && (
            <button type="button" className="dashboard-app-selector-close" onClick={onClose}>
              ×
            </button>
          )}
        </div>

        <div className="dashboard-app-selector-content">
          <section className="dashboard-app-selector-profiles">
            <h3>{t("profileTitle")}</h3>
            <div className="dashboard-app-selector-profile-grid">
              {DASHBOARD_APP_PROFILES.map((profileItem) => (
                <button
                  key={profileItem.id}
                  type="button"
                  className={`dashboard-app-selector-profile-card ${
                    profile === profileItem.id ? "is-selected" : ""
                  }`}
                  onClick={() => applyProfile(profileItem)}
                >
                  <span className="dashboard-app-selector-profile-icon">
                    <ProfileIcon profileId={profileItem.id} />
                  </span>
                  <strong>{t(profileItem.labelKey)}</strong>
                </button>
              ))}
            </div>
          </section>

          <section className="dashboard-app-selector-apps">
            <div className="dashboard-app-selector-section-header">
              <div>
                <h3>{t("appsTitle")}</h3>
                <p>{t("appsSubtitle")}</p>
              </div>
              <button
                type="button"
                className="dashboard-app-selector-link-button"
                onClick={() => setSelectedAppIds(getAllDashboardAppIds())}
              >
                {t("selectAll")}
              </button>
            </div>

            <div className="dashboard-app-selector-app-grid">
              {DASHBOARD_APP_CATALOG.map((app) => {
                const Icon = app.Icon;
                const selected = selectedSet.has(app.id);
                return (
                  <button
                    key={app.id}
                    type="button"
                    className={`dashboard-app-selector-app-card ${selected ? "is-selected" : ""}`}
                    style={{ "--app-gradient": app.gradient }}
                    onClick={() => toggleApp(app.id)}
                  >
                    <span className="dashboard-app-selector-app-check">✓</span>
                    <span className="dashboard-app-selector-app-icon">
                      <Icon />
                    </span>
                    <strong>{t(app.labelKey)}</strong>
                    <small>{t(app.descriptionKey)}</small>
                  </button>
                );
              })}
            </div>
          </section>
        </div>

        <div className="dashboard-app-selector-actions">
          {isOnboarding && (
            <button
              type="button"
              className="dashboard-app-selector-secondary"
              onClick={() => save(getAllDashboardAppIds())}
              disabled={isSaving}
            >
              {t("keepAll")}
            </button>
          )}
          {!isOnboarding && (
            <button
              type="button"
              className="dashboard-app-selector-secondary"
              onClick={onClose}
              disabled={isSaving}
            >
              {t("cancel")}
            </button>
          )}
          <button
            type="button"
            className="dashboard-app-selector-primary"
            onClick={() => save()}
            disabled={isSaving || selectedAppIds.length === 0}
          >
            {isSaving ? t("saving") : t("confirm")}
          </button>
        </div>
      </div>
    </div>
  );
}

export default DashboardAppSelectorModal;
